import { useEffect, useRef, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { cn } from "@/lib/utils";

interface KvCopyProps {
  text: string;
  ariaLabel: string;
}

// 与旧 c2-kv-copy / c2-snippet-copy 等图标按钮一致：
// 复制成功后在按钮加 .copied class 1.5s。无文案切换（按钮内是 SVG）。
export function CopyIcon({ text, ariaLabel, className }: KvCopyProps & { className?: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  const handle = async () => {
    if (!text) return;
    await copyText(text);
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      className={cn(className, copied && "copied")}
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={handle}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="8" y="8" width="13" height="13" rx="2" />
        <path d="M16 8V5.5a2 2 0 0 0-2-2H5.5a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2H8" />
      </svg>
    </button>
  );
}

interface CopyIconLabeledProps {
  text: string;
  ariaLabel: string;
  className?: string;
  label?: string;
}

// install-copy 风格：图标 + "复制" 文案的按钮（一键安装命令复制等）
export function CopyIconLabeled({ text, ariaLabel, className, label = "复制" }: CopyIconLabeledProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  const handle = async () => {
    if (!text) return;
    await copyText(text);
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <button
      type="button"
      className={cn(className, copied && "copied")}
      aria-label={ariaLabel}
      onClick={handle}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="8" y="8" width="13" height="13" rx="2" />
        <path d="M16 8V5.5a2 2 0 0 0-2-2H5.5a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2H8" />
      </svg>
      <span>{copied ? "已复制" : label}</span>
    </button>
  );
}
